import { useEffect, useRef, useState } from 'react';
import type { TaxGroup, TaxZone } from '@/types/tax';
import { isCaProvinceCode } from '@/lib/postal-code';

// ─── County label for hierarchy ───────────────────────────────────────────────

/**
 * Sentinel "county" key used to bucket Canadian zones inside the
 * state → county → city hierarchy. CA postal codes don't map to a
 * county-equivalent (the geo level below province is FSA, which we
 * already surface as the "city" column), so we lift them under a
 * single "Provincial" pseudo-county per province.
 */
export const CA_PROVINCIAL_COUNTY_KEY = 'Provincial';

/**
 * Pick the label we render for a zone's county column.
 *
 * - US zones with a known county → the county name.
 * - CA zones (any province code in stateCode) → `CA_PROVINCIAL_COUNTY_KEY`,
 *   since Canadian tax has no county tier and the prior `'Unknown'`
 *   fallback was misleading the user (NOT-4).
 * - Anything else missing a county → `'Unknown'` (US data gap, e.g.
 *   a stale zone whose Ziptax response never populated geoCounty).
 */
export function countyDisplayLabel(zone: {
  county?: string | null;
  stateCode?: string | null;
}): string {
  if (zone.county && zone.county.trim().length > 0) return zone.county;
  if (isCaProvinceCode(zone.stateCode)) return CA_PROVINCIAL_COUNTY_KEY;
  return 'Unknown';
}

/** True if `countyLabel` is the CA "Provincial" pseudo-county sentinel. */
export function isProvincialCountyLabel(countyLabel: string): boolean {
  return countyLabel === CA_PROVINCIAL_COUNTY_KEY;
}

/**
 * Title-case ALL CAPS proper names produced by Ziptax / the BE name
 * builder, while preserving short acronyms and pre-cased words.
 *
 * Algorithm:
 *   1. Tokenise on whitespace / hyphen / slash boundaries (separators
 *      are preserved in the output).
 *   2. If EVERY word-token is all-caps, treat the string as "shouting"
 *      and title-case every word — this is the common Ziptax-city
 *      case ("ANCHORAGE" → "Anchorage", "NEW YORK" → "New York",
 *      "MIAMI-DADE" → "Miami-Dade").
 *   3. Otherwise (string is already partially cased), only title-case
 *      all-caps words LONGER than 3 chars; leave short all-caps words
 *      alone as likely acronyms ("GST", "PST", "HST", "MB"). This
 *      handles BE-generated labels like "City of OACOMA" →
 *      "City of Oacoma" without mangling "GST (Federal)" or
 *      "PST - Manitoba".
 *
 * Words containing any lowercase letter are always left alone
 * ("McAllen", "Manhattan", "(Federal)").
 */
export function titleCaseName(name: string): string {
  if (!name) return name;
  const parts = name.split(/(\s+|[-/])/);
  const wordTokens = parts.filter((p) => /[A-Za-z]/.test(p));
  // Empty / whitespace-only strings have no word tokens to inspect —
  // return verbatim so the original "" / " " survives the round-trip.
  if (wordTokens.length === 0) return name;
  const everyWordAllCaps = wordTokens.every((w) => /^[A-Z]+$/.test(w));
  const minLenInMixed = 4; // acronyms in mixed strings are <=3 chars
  return parts
    .map((part) => {
      if (!part || !/[A-Za-z]/.test(part)) return part; // separator
      if (/[a-z]/.test(part)) return part; // already-cased word
      // All-caps word from here on.
      if (everyWordAllCaps || part.length >= minLenInMixed) {
        return part.charAt(0) + part.slice(1).toLowerCase();
      }
      return part; // short acronym inside a mixed string
    })
    .join('');
}

/**
 * Country-aware "ZIP" / "FSA" / generic-fallback terminology with
 * grammatically-correct pluralisation. The US uses USPS ZIP codes;
 * Canada uses Forward Sortation Areas (the first three chars of a
 * postal code). A tenant with zones in both countries can't be
 * accurately labelled by either noun, so it falls back to the neutral
 * "postal code(s)".
 *
 *     postalLabel('US', 1)    → 'ZIP'
 *     postalLabel('US', 12)   → 'ZIPs'
 *     postalLabel('CA', 1)    → 'FSA'
 *     postalLabel('CA', 12)   → 'FSAs'
 *     postalLabel('MIX', 1)   → 'postal code'
 *     postalLabel('MIX', 12)  → 'postal codes'
 */
export function postalLabel(country: 'US' | 'CA' | 'MIX', count: number): string {
  if (country === 'MIX') return count === 1 ? 'postal code' : 'postal codes';
  const noun = country === 'CA' ? 'FSA' : 'ZIP';
  return count === 1 ? noun : `${noun}s`;
}

/**
 * Classify the tax-zone set by region. Returns 'US' when every zone is
 * US-shaped, 'CA' when every zone is CA-shaped (province codes), and
 * 'MIX' when both are present. Empty set returns 'US' as the neutral
 * default — the chip strip's single-tenant common case.
 *
 * Detects Canadian zones via `isCaProvinceCode`. ALL stateCode values
 * must be checked; a single CA zone in a mostly-US tenant should still
 * flip to MIX so the chip strip reads honestly.
 */
export function classifyZoneRegion(
  zones: Array<{ stateCode?: string | null }>,
): 'US' | 'CA' | 'MIX' {
  let hasUs = false;
  let hasCa = false;
  for (const z of zones) {
    if (isCaProvinceCode(z.stateCode)) hasCa = true;
    else if (z.stateCode) hasUs = true;
    if (hasUs && hasCa) return 'MIX';
  }
  if (hasCa && !hasUs) return 'CA';
  return 'US';
}

/**
 * Country-aware "county" / "province" terminology with pluralisation.
 * For Canada we surface the "Provincial" pseudo-county as one
 * province bucket (FSAs are the lower tier).
 *
 *     hierarchyLabel('US', 1)  → 'county'
 *     hierarchyLabel('US', 2)  → 'counties'
 *     hierarchyLabel('CA', 1)  → 'province'
 *     hierarchyLabel('CA', 2)  → 'provinces'
 */
export function hierarchyLabel(country: 'US' | 'CA', count: number): string {
  if (country === 'CA') return count === 1 ? 'province' : 'provinces';
  return count === 1 ? 'county' : 'counties';
}

// ─── No-Ziptax-data placeholder ───────────────────────────────────────────────

/**
 * True when a zone is a "Ziptax had no data for this ZIP" placeholder.
 *
 * Two signals — either is sufficient, because populate writes both in
 * the same transaction:
 *
 *   1. `zone.refreshStatus === 'no_ziptax_data'` (the canonical signal).
 *   2. `zone.taxGroup.placeholderReason === 'NO_ZIPTAX_DATA'` (defense
 *      in depth — surfaces the same condition even on snapshots /
 *      cached payloads that pre-date the refreshStatus enum addition).
 *
 * The display layer reads this to render the amber "Ziptax: no data"
 * badge plus a muted 0.00% rate, instead of falsely showing the row
 * as a real zero-rate jurisdiction.
 */
export function isNoZiptaxDataZone(zone: TaxZone): boolean {
  if (zone.refreshStatus === 'no_ziptax_data') return true;
  const group = zone.taxGroup;
  if (group?.placeholderReason === 'NO_ZIPTAX_DATA') return true;
  return false;
}

/**
 * True when EVERY zone in a list is a no-data placeholder. Used by the
 * state-row aggregator to flip the entire row's display into placeholder
 * mode — Brian wants a single visible signal at every hierarchy level.
 */
export function allNoZiptaxDataZones(zones: TaxZone[]): boolean {
  return zones.length > 0 && zones.every((z) => isNoZiptaxDataZone(z));
}

// ─── Populating placeholder (Ziptax fetch in flight) ──────────────────────────

/**
 * True when a zone is a "fetch in flight" placeholder inserted by the
 * bulk service-area save controller. The placeholder gives the user
 * instant visual feedback that work is happening; the populate worker
 * supersedes it with the real Ziptax-derived group when it finishes
 * (`refreshStatus` flips to `current` or `no_ziptax_data`).
 *
 * The single canonical signal is `refreshStatus === 'populating'`.
 * Unlike the no-data path we don't ALSO check `placeholderReason`
 * because (a) the populating group is a per-tenant sentinel rather
 * than a per-state row, so a row could carry the reason after its
 * status already advanced; (b) older cached payloads don't have this
 * status anyway. Status is sufficient.
 */
export function isPopulatingZone(zone: TaxZone): boolean {
  return zone.refreshStatus === 'populating';
}

/**
 * True when ANY zone in the supplied list is a populating placeholder.
 * The list panel uses this to drive the React Query `refetchInterval`
 * — polling stops automatically once nothing is in flight.
 */
export function hasAnyPopulatingZone(zones: TaxZone[]): boolean {
  return zones.some((z) => isPopulatingZone(z));
}

// ─── Misc helpers ─────────────────────────────────────────────────────────────

export function idOf(record: { id?: string }): string {
  return record.id ?? '';
}

export function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

export function titleCase(value: string): string {
  return value
    .split('_')
    .join(' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Render an applies-to value (single string or array) as a human label.
 * Multi-dim (Brian's backlog #41): ['materials','labor'] → "Materials, Services"
 * (the wire value stays 'labor'; "Services" is the user-facing term).
 * `['all']` collapses to "All". Unknown values pass through via titleCase.
 */
export function appliesToLabel(appliesTo: string | string[] | null | undefined): string {
  if (appliesTo == null) return 'None';
  const set = coerceAppliesToSet(appliesTo);
  if (set.length === 0) return 'None';
  if (set.includes('all')) return 'All';
  const map: Record<string, string> = {
    all: 'All',
    materials: 'Materials',
    // "Services" is our user-facing term for the labor category (the wire
    // value stays 'labor').
    labor: 'Services',
    freight: 'Freight',
  };
  return set.map((v) => map[v] ?? titleCase(v)).join(', ');
}

/**
 * Coerce an applies-to wire shape (single string, array, or null) into
 * a canonical string[] set. Used at every FE read boundary so the rest
 * of the code only deals with arrays.
 */
export function coerceAppliesToSet(input: string | string[] | null | undefined): string[] {
  if (input == null) return [];
  if (Array.isArray(input)) {
    return input.map((v) => String(v).trim()).filter((v) => v.length > 0);
  }
  const trimmed = String(input).trim();
  return trimmed.length === 0 ? [] : [trimmed];
}

/**
 * True when the multi-dim applies-to set contains the given category.
 * `'all'` is a wildcard — matches every category.
 */
export function appliesToIncludes(
  appliesTo: string | string[] | null | undefined,
  category: string,
): boolean {
  const set = coerceAppliesToSet(appliesTo);
  if (set.length === 0) return false;
  if (set.includes('all')) return true;
  return set.includes(category);
}

// ─── Debounce ─────────────────────────────────────────────────────────────────

export function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    timer.current = setTimeout(() => setDebounced(value), delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [value, delay]);
  return debounced;
}

// ─── Jurisdiction rate ────────────────────────────────────────────────────────

export function getJurisdictionRate(
  zone: TaxZone,
  groupsById: Map<string, TaxGroup>,
  jType: string,
): number | null {
  const gId = zone.taxGroupId ?? (zone.taxGroup ? idOf(zone.taxGroup) : null);
  const group = gId ? groupsById.get(gId) : null;
  if (!group?.items?.length) return null;
  // Use the same hierarchy matcher as findOverrideTargetCode so Canadian
  // codes (province/federal) surface their rate when the UI asks for the
  // 'state' or 'city' tier. Without this, getJurisdictionRate(zone, _, 'city')
  // returned null for BC's city-column row because PST is jurisdictionType='province'
  // — so the row showed no rate even when an override was set.
  const allowed =
    jType === 'state' || jType === 'county' || jType === 'city'
      ? HIERARCHY_TYPE_MATCHERS[jType]
      : [jType];
  const matching = group.items.filter((item) =>
    item.taxCode?.jurisdictionType ? allowed.includes(item.taxCode.jurisdictionType) : false,
  );
  if (matching.length === 0) return null;
  return matching.reduce(
    (sum, item) =>
      sum + (item.taxCode.effectiveRate ?? item.taxCode.rateOverride ?? item.taxCode.rate ?? 0),
    0,
  );
}

// ─── Effective rate (with group override) ─────────────────────────────────────

export function effectiveRateForZone(
  zone: TaxZone,
  groupsById: Map<string, TaxGroup>,
): { rate: number; hasOverride: boolean } {
  const gId = zone.taxGroupId ?? (zone.taxGroup ? idOf(zone.taxGroup) : null);
  const group = gId ? groupsById.get(gId) : null;
  if (!group?.items?.length) return { rate: zone.combinedRate, hasOverride: false };
  const hasOverride = group.items.some((item) => item.taxCode?.isOverridden);
  const effective = group.items.reduce(
    (sum, item) => sum + (item.taxCode?.effectiveRate ?? item.taxCode?.rate ?? 0),
    0,
  );
  return { rate: effective, hasOverride };
}

// ─── Hierarchy-aware tax-code finder ──────────────────────────────────────────

/**
 * Maps a UI hierarchy level ("state", "county", "city") to the set of
 * Prisma `jurisdictionType` enum values that should match. CA tax data
 * uses `province` + `federal` at the state tier, which is why the row
 * lookups in `TaxZoneListPanel` / `TaxCountyPanel` used to silently miss
 * every Canadian zone and fall into create-mode (defect #1).
 *
 * - `state` → matches US `state` as well as CA `province` + `federal`,
 *   so clicking the BC row finds GST/PST instead of asking the user to
 *   create a brand-new code.
 * - `county` → US `county` and CA `province` (since the CA "Provincial"
 *   pseudo-county shares the same code as the state tier).
 * - `city` → just `city`; CA has no city-tier codes today.
 */
const HIERARCHY_TYPE_MATCHERS: Record<'state' | 'county' | 'city', string[]> = {
  state: ['state', 'province', 'federal'],
  county: ['county', 'province'],
  city: ['city', 'province'],
};

/**
 * Find the tax_code on this row that's the most natural override
 * target. When multiple codes match the hierarchy level (e.g. BC has
 * GST + PST both at the "state" tier), prefer the one that already
 * carries a per-tenant override; otherwise pick the non-federal one
 * (PST > GST for BC, so the user can override the provincial component
 * without touching the federal one), falling back to whichever comes
 * first by `sortOrder`.
 *
 * This finder ONLY considers codes at the requested level (per
 * `HIERARCHY_TYPE_MATCHERS`). If no code matches the level, returns
 * `null`. That `null` is what the panels use to decide whether to paint
 * the row's restore-icon / amber badge — both are tied to "this level
 * actually carries an override". The pencil's edit target uses
 * `findEditTargetCode` instead, which walks a fallback chain.
 */
export function findOverrideTargetCode(
  group: TaxGroup | null | undefined,
  level: 'state' | 'county' | 'city',
): TaxGroup['items'][number] | null {
  if (!group?.items?.length) return null;
  return pickFromMatches(group, HIERARCHY_TYPE_MATCHERS[level]);
}

/**
 * Ordered fallback chain for `findEditTargetCode`. Each entry is a
 * tier — the finder walks them in order until it finds a matching
 * tax_code. This mirrors the backend's precedence walk
 * (ZIP > city > county > state/provincial > federal — see
 * `tax-resolution.service.ts`) so the pencil edits whatever code is
 * actually contributing to the row's displayed rate.
 *
 * The motivating bug: Bronx (10454/10455) is stored as a tax_group with
 * ONLY a `state` code (NY State Tax, 4% + 5% override) and a `city` code
 * (NY NEW YORK City, 4.5%). The row sums those into the 9.50% the user
 * sees on the county column, but no `county` jurisdictionType exists.
 * Without a fallback, the county pencil dropped into create-mode at 0%
 * — confusing, and a "Create" click would have double-counted the rate.
 *
 * The walk is per-level rather than a single bigger matcher list because
 * the order of preference matters (county before state), and because we
 * must NOT bleed across tiers in ways that overlap another row's pencil
 * (e.g. county should never edit the city code — that's the city
 * pencil's job).
 *
 *  - `state` → state-tier only. State row never falls through.
 *  - `county` → **strict** county-tier only. Earlier this fell back to
 *    `['state', 'federal']` so NYC boroughs (Queens/Bronx/Kings — which
 *    have no county-tier code because NYC's county tax is rolled into
 *    state + city) had their pencil open EDIT on NY State. Saving a rate
 *    there set `rateOverride` on the shared NY State `tax_code`, which
 *    leaked the override to every NY ZIP across every county group.
 *    Strict county-tier match means `TaxCountyPanel` decides between
 *    edit (county code present) and create (county code missing → ADD a
 *    county-tier code at 0%, forked off this row's ZIPs so the new code
 *    doesn't propagate). This is the symmetric treatment of the city
 *    pencil fix (PR #620 + #622). DO NOT re-introduce a state fallback
 *    "for symmetry" with the legacy Bronx behavior — the fork-on-add
 *    flow is the correct shape.
 *  - `city` → **strict** city-tier only. Earlier this fell back to
 *    county/state (PR #618) so Alden Manor's pencil opened EDIT on
 *    NASSAU. Brian: that's wrong — the city row's pencil should ADD a
 *    city-tier code (rate 0, linked into the group via the BE's new
 *    `groupId` create path) rather than edit a sibling tier. The strict
 *    return makes `TaxCityPanel` decide between edit and create itself.
 */
const EDIT_FALLBACK_CHAIN: Record<'state' | 'county' | 'city', readonly (readonly string[])[]> = {
  state: [HIERARCHY_TYPE_MATCHERS.state],
  county: [HIERARCHY_TYPE_MATCHERS.county],
  city: [HIERARCHY_TYPE_MATCHERS.city],
};

/**
 * Find the tax_code the pencil should open for edit. Falls back up the
 * jurisdiction hierarchy when the row's own tier has no code — see
 * `EDIT_FALLBACK_CHAIN`. Returns `null` only when the group has zero
 * matching items across the entire fallback chain (true empty case).
 *
 * This is the function the row's pencil button binds to. Distinct from
 * `findOverrideTargetCode` because the override / restore-icon decision
 * MUST only consider the row's own tier — otherwise the county row's
 * restore icon would offer to restore the state code, which belongs to
 * the state row.
 */
export function findEditTargetCode(
  group: TaxGroup | null | undefined,
  level: 'state' | 'county' | 'city',
): TaxGroup['items'][number] | null {
  if (!group?.items?.length) return null;
  for (const allowed of EDIT_FALLBACK_CHAIN[level]) {
    const picked = pickFromMatches(group, allowed);
    if (picked) return picked;
  }
  return null;
}

function pickFromMatches(
  group: TaxGroup,
  allowed: readonly string[],
): TaxGroup['items'][number] | null {
  const matches = group.items.filter(
    (item) => item.taxCode?.jurisdictionType && allowed.includes(item.taxCode.jurisdictionType),
  );
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0]!;

  const sorted = [...matches].sort((a, b) => a.sortOrder - b.sortOrder);
  // Already-overridden codes go first — the user's intent is most
  // likely to tweak whatever they previously customized. We treat both
  // rate-override and applies-to-override as "customized" because
  // either flips the row chip into amber on the UI.
  const overridden = sorted.find(
    (item) => item.taxCode?.isOverridden || item.taxCode?.isAppliesToOverridden,
  );
  if (overridden) return overridden;
  // Non-federal next (CA: PST/HST before GST), so the BC scenario
  // surfaces the PST when the user clicks the Provincial row.
  const nonFederal = sorted.find((item) => item.taxCode?.jurisdictionType !== 'federal');
  if (nonFederal) return nonFederal;
  return sorted[0]!;
}
